import { useEffect, useRef } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useToastStore, type Toast as ToastType } from '../../store/toastStore';

const icons = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

function ToastItem({ toast, onDismiss }: { toast: ToastType; onDismiss: () => void }) {
  const Icon = icons[toast.type];
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleAnimationEnd = (e: AnimationEvent) => {
      if (e.animationName === 'slideOut') {
        onDismiss();
      }
    };

    element.addEventListener('animationend', handleAnimationEnd);
    return () => element.removeEventListener('animationend', handleAnimationEnd);
  }, [onDismiss]);

  return (
    <div
      ref={ref}
      className={`toast toast-${toast.type}`}
      role="alert"
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
    >
      <Icon className="toast-icon" aria-hidden="true" />
      <span className="toast-message">{toast.message}</span>
      {toast.action && (
        <button
          onClick={() => {
            toast.action?.onClick();
            ref.current?.classList.add('toast-dismissing');
          }}
          className="toast-action"
          type="button"
        >
          {toast.action.label}
        </button>
      )}
      <button
        onClick={() => {
          ref.current?.classList.add('toast-dismissing');
        }}
        className="toast-close"
        aria-label="Dismiss notification"
      >
        <X className="icon icon-sm" aria-hidden="true" />
      </button>
    </div>
  );
}

export function Toast() {
  const { toasts, removeToast } = useToastStore();

  if (toasts.length === 0) return null;

  return (
    <div className="toast-container" role="region" aria-label="Notifications">
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onDismiss={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
}
